import ListWorkflowHistory from '@/graphql/queries/ListWorkflowHistory'
import client from '@/graphql/client'
import OnUpdateCards from '@/graphql/subscriptions/OnUpdateCards'
import GetWorkflowHistory from '@/graphql/queries/GetWorkflowHistory'
import TagWorkflowVersion from '@/graphql/mutations/TagWorkflowVersion'
import { addToSubscriptions } from '@/api/subscriptionHelpers'

export const workflowHistory = {
  namespaced: true,
  state: {
    workflowHistory: null,
    nextToken: null,
    taggedHistory: null,
    taggedNextToken: null,
    subscriptions: null
  },
  mutations: {
    setWorkflowHistory (state, { workflowHistory, workflow, nextToken }) {
      state.workflowHistory = workflowHistory
      state.nextToken = nextToken
      if (workflow && state.workflowHistory?.findIndex((historyRecord) => historyRecord.version === workflow.version) === -1) {
        // add current version to the top of the history
        state.workflowHistory.unshift({
          id: workflow.id,
          updatedAt: workflow.updatedAt,
          updateNote: workflow.updateNote,
          version: workflow.version,
          tag: null
        })
      }
    },
    appendWorkflowHistory (state, { items, nextToken }) {
      if (state.workflowHistory) {
        state.workflowHistory.push(...items)
      } else {
        state.workflowHistory = items
      }
      state.nextToken = nextToken
    },
    setTaggedHistory (state, { items, nextToken }) {
      state.taggedHistory = items
      state.taggedNextToken = nextToken
    },
    appendTaggedHistory (state, { items, nextToken }) {
      if (state.taggedHistory) {
        state.taggedHistory.push(...items)
      } else {
        state.taggedHistory = items
      }
      state.taggedNextToken = nextToken
    },
    clearTaggedHistory (state) {
      state.taggedHistory = null
      state.taggedNextToken = null
    },
    setSubscriptions (state, payload) {
      state.subscriptions = payload
    },
    unsubscribeFromAll (state) {
      if (state.subscriptions) {
        for (const subscription of state.subscriptions) {
          subscription.unsubscribe()
        }
      }
      state.subscriptions = null
    },
    addHistoryRecord (state, payload) {
      // Ignore updates for workflows we're not tracking
      if (!state.workflowHistory || state.workflowHistory.length === 0) return

      // Check if this is the workflow we're tracking (compare with first record's id)
      const trackedWorkflowId = state.workflowHistory[0]?.id
      if (trackedWorkflowId && payload.id !== trackedWorkflowId) return

      const addVersion = payload.version
      const index = state.workflowHistory.findIndex((workflow) => workflow.version === addVersion)
      if (index >= 0) {
        // replace existing record, preserve tag if it exists
        const existingTag = state.workflowHistory[index].tag
        state.workflowHistory[index] = {
          id: payload.id,
          updatedAt: payload.updatedAt,
          updateNote: payload.updateNote,
          version: payload.version,
          tag: payload.tag !== undefined ? payload.tag : existingTag
        }
      } else {
        // check for gaps in the history
        if (state.workflowHistory.length > 0 && addVersion >= 3) {
          if (state.workflowHistory.findIndex((workflow) => workflow.version === addVersion - 2) === -1) {
            // found a gap, throw an error
            throw new Error('Gap in workflow history')
          }
        }
        // add current version to the top of the history
        state.workflowHistory.unshift({
          id: payload.id,
          updatedAt: payload.updatedAt,
          updateNote: payload.updateNote,
          version: payload.version,
          tag: payload.tag || null
        })
      }
    },
    updateVersionTag (state, { version, tag }) {
      // Update in main history
      const index = state.workflowHistory?.findIndex((workflow) => workflow.version === version)
      if (index >= 0) {
        state.workflowHistory[index].tag = tag
      }
      // Update in tagged history
      if (tag) {
        const taggedIndex = state.taggedHistory?.findIndex((workflow) => workflow.version === version)
        if (taggedIndex >= 0) {
          state.taggedHistory[taggedIndex].tag = tag
        } else if (state.taggedHistory) {
          // Add to tagged history if not present
          const record = state.workflowHistory?.[index]
          if (record) {
            state.taggedHistory.unshift({ ...record, tag })
          }
        }
      } else {
        // Remove from tagged history if tag is removed
        state.taggedHistory = state.taggedHistory?.filter((workflow) => workflow.version !== version) || null
      }
    }
  },
  actions: {
    async listWorkflowHistory (
      { commit, dispatch, rootState },
      { projectId, workflowId, workflow, subscribe = true, nextToken = null, limit = 100 }
    ) {
      try {
        // Skip this query if user is anonymous and in view only mode
        if ((!rootState.workflow.workflow && !workflow) || rootState.workflow.isViewOnlyEnabled === true) {
          return
        }
        const res = await client.query({
          operationName: 'listWorkflowHistory',
          query: ListWorkflowHistory,
          variables: {
            projectId,
            workflowId,
            limit,
            nextToken
          }
        })

        const historyData = res?.listWorkflowHistory?.workflowHistory
        const items = historyData?.items || []
        const responseNextToken = historyData?.nextToken || null

        if (nextToken) {
          commit('appendWorkflowHistory', {
            items,
            nextToken: responseNextToken
          })
        } else {
          commit('setWorkflowHistory', {
            workflowHistory: items,
            workflow: rootState.workflow.workflow || workflow,
            nextToken: responseNextToken
          })
        }

        if (rootState.isUserLoggedIn === false) {
          // don't subscribe if user is not logged in or view only mode is enabled
          return
        }

        if (subscribe) {
          // Unsubscribe from previous subscriptions before creating new ones
          commit('unsubscribeFromAll')

          const subscriptions = []
          const addSubscription = (query, variables, callback) => {
            const sub = addToSubscriptions(query, variables, callback)
            subscriptions.push(sub)
          }

          // OnCreateOrUpdateWorkflow subscription removed - handled by workflows module
          // which notifies this module via cross-module commit
          // The addHistoryRecord mutation will ignore updates for other workflows

          addSubscription(OnUpdateCards, { workflowId }, (data) => {
            try {
              commit('addHistoryRecord', {
                id: data.onUpdateCards.workflowId,
                ...data.onUpdateCards
              })
            } catch (e) {
              console.log(e)
              dispatch('listWorkflowHistory', { projectId, workflowId, subscribe: false })
            }
          })

          commit('setSubscriptions', subscriptions)
        }
      } catch (e) {
        console.log(e)
      }
    },
    async listTaggedHistory ({ commit, rootState }, { projectId, workflowId, nextToken = null, limit = 100 }) {
      try {
        if (rootState.workflow.isViewOnlyEnabled === true) {
          return
        }

        const res = await client.query({
          operationName: 'listWorkflowHistory',
          query: ListWorkflowHistory,
          variables: {
            projectId,
            workflowId,
            limit,
            taggedOnly: true,
            nextToken
          }
        })

        const historyData = res?.listWorkflowHistory?.workflowHistory
        const items = historyData?.items || []
        const responseNextToken = historyData?.nextToken || null

        if (nextToken) {
          commit('appendTaggedHistory', {
            items,
            nextToken: responseNextToken
          })
        } else {
          commit('setTaggedHistory', {
            items,
            nextToken: responseNextToken
          })
        }
      } catch (e) {
        console.log(e)
      }
    },
    async getWorkflowHistory ({ commit }, { projectId, workflowId, version }) {
      const res = await client.query({
        operationName: 'getWorkflowHistory',
        query: GetWorkflowHistory,
        variables: {
          projectId,
          workflowId,
          version
        }
      })

      return res.getWorkflowHistory
    },
    async tagWorkflowVersion ({ commit }, { projectId, workflowId, version, tag }) {
      const res = await client.query({
        operationName: 'tagWorkflowVersion',
        query: TagWorkflowVersion,
        variables: {
          projectId,
          workflowId,
          version,
          tag
        }
      })

      commit('updateVersionTag', { version, tag })

      return res.tagWorkflowVersion
    }
  }
}
